import {WorkspaceCapabilityType} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/workspaceCapabilities/domain/WorkspaceCapabilityType";
import AddIcon from "@atlaskit/icon/core/add";
import ChevronRightIcon from "@atlaskit/icon/core/chevron-right";
import CrossIcon from "@atlaskit/icon/core/cross";
import ShowMoreVerticalIcon from "@atlaskit/icon/core/show-more-vertical";
import DownloadIcon from "@atlaskit/icon/core/download";
import UploadIcon from "@atlaskit/icon/core/upload";
import {token} from "@atlaskit/tokens";
import {useMutation, useQueryClient, type InfiniteData} from "@tanstack/react-query";
import {createFileRoute, useNavigate, useSearch} from "@tanstack/react-router";
import {useCallback, useEffect, useMemo, useState} from "react";
import {z} from "zod";
import {BottomDrawer} from "~/components/BottomDrawer";
import {useOptionalWorkspaceCapabilityConfiguration} from "~/hooks/workspaceConfiguration";
//import {BtlActivityViewPanel} from "~/components/btlActivityComponents/btlActivityViewPanel";
import {
    BtlActivityViewPanelAlternative,
    CancelEventConfirmationModal,
} from "~/components/btlActivityComponents/btlActivityViewPanelAlternative";
import {CsvUploadComponent} from "~/components/btlActivityComponents/csvUploadComponent";
import {BtlKanbanBoardCard} from "~/components/btlActivityComponents/kanbanBoardCard";
import {CopyDataDropdownItem} from "~/components/dataActions/CopyDataDropdownItem";
import {ExportDataDropdownItem} from "~/components/dataActions/ExportDataDropdownItem";
import {ExportOptionsDrawerContent} from "~/components/dataActions/ExportOptionsDrawerContent";
import {InviteListOptionsDrawerContent} from "~/components/dataActions/InviteListOptionsDrawerContent";
import {OttopilotColumnType, type OttopilotColumn} from "~/components/ottopilotDataViewer/column";
import {OttopilotDataViewer} from "~/components/ottopilotDataViewer/ottopilotDataViewer";
import {useOttopilotDataViewerController} from "~/components/ottopilotDataViewer/ottopilotDataViewerController";
import {useOttopilotExportActions} from "~/components/ottopilotDataViewer/useOttopilotExportActions";
import {RowEngagementPanelType, RowEngagementType} from "~/components/ottopilotDataViewer/rowEngagementPanel";
import {SortDirection} from "~/components/ottopilotDataViewer/sortConfiguration";
import {ViewType} from "~/components/ottopilotDataViewer/view";
import {TableOnlyPage} from "~/components/tableOnlyPage";
import {AnankeDropdownMenu} from "@vesta/ananke/components/anankeDropdownMenu";
import {AnankeDropdownItemGroup} from "@vesta/ananke/components/anankeDropdownItemGroup";
import {AnankeDropdownItem} from "@vesta/ananke/components/anankeDropdownItem";
import {useOttopilotConfiguration} from "~/contexts/ottopilotConfigurationContext";
import {useTimezones} from "~/contexts/timezonesContext";
import {useWorkspace} from "~/contexts/workspaceContext";
import {useEventTemplates} from "~/contexts/workspaceEventTemplatesContext";
import {useOrganizationalUnits} from "~/contexts/organizationalUnitsContext";
import {useOrganizationalUnitTemplates} from "~/contexts/workspaceContext";
import {useWorkspaceUser} from "~/contexts/workspaceUserContext";
import {useUsers} from "~/contexts/workspaceUsersContext";
import {useWorkspaceUserTemplates as useUserTemplates} from "~/contexts/workspaceContext";
import {useBtlActivityTemplates} from "~/contexts/workspaceBtlActivityTemplatesContext";
import type {ScopeFilterConfig} from "@vesta/vos-types/modules/workspaceLevel/domain/OttopilotDataViewerConfig";
import {
    BtlActivityStatus,
    BtlActivityTemplates,
    BTL_ACTIVITIES_OTTOPILOT_VIEWER_INSTANCE_ID,
    assignmentModeToDisplayName,
    btlActivityTemplateToDisplayName,
    btlEventStatusToDisplayName,
    btlReviewItemApiResponseArraySchema,
    convertBtlActivityApiTypeToBtlActivity,
    type BtlActivity,
    type BtlActivityApiType,
    type BtlReviewItemApiResponseItem,
} from "~/domainModels/btlActivities";
import {resolveBtlActivityRoleCapabilities, resolveBtlActivityUserRole} from "~/domainModels/btlActivityRoles";
import {addBtlReviewItems, clearAllBtlReviewItems} from "~/domainModels/btlReviewStore";
import {transformApiResponseToDatabaseItems} from "~/domainModels/btlReviewTransformers";
import {CreateBtlEventAdaptiveDialog} from "~/domainModels/dhiraagu/createAdaptiveDialog";
import {
    DhiraaguEventTypes,
    getIslandNameFromGeographyNodes,
    getIslandNamesFromOuIds,
    getIslandOuIdsFromGeographyNodes,
} from "~/domainModels/workspaceSpecific/dhiraagu";
import type {SharedCalendarViewConfig} from "@vesta/vos-types/modules/workspaceLevel/domain/SharedCalendarViewConfig";
import {useCheckMobile} from "~/hooks/useCheckMobile";

import {DhiraaguEnquiryCreateFormEmbedded} from "~/workspaceConfig/hostedForms/dhiraaguEnquiryCreate/-routeComponent";
import {DhiraaguLogCallFormEmbedded} from "~/workspaceConfig/hostedForms/dhiraaguCallLog/-routeComponent";
import {DhiraaguSaleCreateFormEmbedded} from "~/workspaceConfig/hostedForms/dhiraaguSaleCreate/-routeComponent";

import {CreateBtlActivityComponent} from "~/routes/_withAuthenticationStatus/_authenticated/_withProfileStatus/_profileCompleted/w/$workspaceId/ottopilot/_ottopilotScaffold/btl-activities/index/-btlActivityCreateComponent";
import {AnankeButton} from "@vesta/ananke/components/anankeButton";
import {useAnankeFlags} from "@vesta/ananke/hooks/useAnankeFlags";
import type {Uuid} from "@vesta/common-type-definitions/typeDefinitions";
import {robustJsonResultClientFetch} from "@vesta/error-handling-remix/fetches/robustJsonResultClientFetch";
import {dataResult, errorResult, type Result} from "@vesta/error-handling-remix/result";
import {generateUuid} from "@vesta/global-common-typescript/utilities/utilities";
import {cancelBtlActivity, getBtlActivities, getBtlActivities_Length, getBtlActivity_ColumnValues} from "~/utilities/apis/api";
import {WORKSPACE__BTL_ACTIVITIES__QUERY_KEY} from "~/utilities/queryKeys";
import {
    resolveCalendarAllDayStyle,
    resolveCalendarEventTypeColor,
    resolveCalendarEventTypeLabel,
    resolveCalendarLegendItems,
    resolveCalendarTemplateStrokeColor,
} from "~/utilities/sharedCalendarConfig";
import {

    formatDateTime,
    getUserDisplayName,
    transformRowForExport,
} from "~/utilities/utilities";
import {uniqueUuid} from "@vesta/unique/unique";
import {useShowErrorFlag} from "~/hooks/useShowErrorFlag";

const BTL_CSV_TEMPLATE_CONFIG = {
    headers: ["Title", "Description", "Timezone", "Start Time", "Start Date", "End Time", "End Date", "Is Allday Event", "Geography Nodes", "Type"],
    sampleData: ["First BTL", "First door to door btl", "Asia/Baghdad", "13:00", "17-01-2026", "15:00", "18-01-2026", "TRUE", "Male' City", "Door To Door"],
    fileName: "btl_activity_template.csv",
};

const BTL_ACTIVITIES_VIEWER_QUERY_KEY_PREFIX = BTL_ACTIVITIES_OTTOPILOT_VIEWER_INSTANCE_ID;

const btlActivitiesSearchSchema = z.object({
    view: z.enum(["table", "kanban", "calendar", "create"]).optional().default("table"),
});

export const Route = createFileRoute(
    "/_withAuthenticationStatus/_authenticated/_withProfileStatus/_profileCompleted/w/$workspaceId/ottopilot/_ottopilotScaffold/btl-activities/",
)({
    validateSearch: (search: Record<string, unknown>) => btlActivitiesSearchSchema.parse(search),
    component: RouteComponent,
});

const BTL_TABLE_VIEW_ID = uniqueUuid`f74f6723-d5a2-4d84-9733-d1676d2408c1`;
const BTL_KANBAN_VIEW_ID = uniqueUuid`a1b2c3d4-e5f6-4a7b-8c9d-0e1f2a3b4c5d`;
const BTL_CALENDAR_VIEW_ID = uniqueUuid`b2c3d4e5-f6a7-4b8c-9d0e-1f2a3b4c5d6e`;

const DEFAULT_BTL_CALENDAR_CONFIG: SharedCalendarViewConfig = {
    eventTypeLabelMap: {
        [BtlActivityStatus.draft]: "Draft",
        [BtlActivityStatus.active]: "Live",
        [BtlActivityStatus.scheduled]: "Scheduled",
        [BtlActivityStatus.completed]: "Completed",
        [BtlActivityStatus.cancelled]: "Cancelled",
        deleted: "Deleted",
    },
    legendItems: [
        {type: BtlActivityStatus.draft, label: "Draft", description: "Event is in draft status"},
        {type: BtlActivityStatus.active, label: "Live", description: "Event is currently active"},
        {type: BtlActivityStatus.scheduled, label: "Scheduled", description: "Event is scheduled for the future"},
        {type: BtlActivityStatus.completed, label: "Completed", description: "Event has been completed"},
        {type: BtlActivityStatus.cancelled, label: "Cancelled", description: "Event has been cancelled"},
        {type: "deleted", label: "Deleted", description: "Event has been deleted"},
    ],
    eventTypeColorMap: {
        [BtlActivityStatus.draft]: {
            backgroundColorToken: "color.background.accent.gray.subtler",
            borderColorToken: "color.border.accent.gray",
            textColorToken: "color.text.accent.gray",
        },
        [BtlActivityStatus.active]: {
            backgroundColorToken: "color.background.accent.blue.subtlest",
            borderColorToken: "color.border.accent.blue",
            textColorToken: "color.text.accent.blue",
        },
        [BtlActivityStatus.scheduled]: {
            backgroundColorToken: "color.background.accent.yellow.subtlest",
            borderColorToken: "color.border.accent.yellow",
            textColorToken: "color.text.accent.yellow",
        },
        [BtlActivityStatus.completed]: {
            backgroundColorToken: "color.background.accent.green.subtlest",
            borderColorToken: "color.border.accent.green",
            textColorToken: "color.text.accent.green",
        },
        [BtlActivityStatus.cancelled]: {
            backgroundColorToken: "color.background.accent.purple.subtlest",
            borderColorToken: "color.border.accent.purple",
            textColorToken: "color.text.accent.purple",
        },
        deleted: {
            backgroundColorToken: "color.background.accent.red.subtlest",
            borderColorToken: "color.border.accent.red",
            textColorToken: "color.text.accent.red",
        },
    },
    templateStrokeColorMap: {
        [BtlActivityTemplates.doorToDoor]: "color.border.accent.blue",
        [BtlActivityTemplates.leadCaptureBooth]: "color.border.accent.purple",
        [BtlActivityTemplates.roadshow]: "color.border.accent.teal",
        [BtlActivityTemplates.referralDriveCampaign]: "color.border.accent.orange",
        [BtlActivityTemplates.dealerFootfallBooster]: "color.border.accent.green",
    },
    allDayStyle: {mode: "fixedGreen"},
};

function canCancelBtlActivity({
    row,
    currentUserId,
    currentUserTemplateId,
}: {
    row: BtlActivity;
    currentUserId: Uuid;
    currentUserTemplateId: Uuid | null;
}): boolean {
    if (row.btlStatus === BtlActivityStatus.cancelled || row.btlStatus === BtlActivityStatus.completed) {
        return false;
    }
    const role = resolveBtlActivityUserRole({
        row,
        currentUserId,
        currentUserTemplateId,
    });
    const capabilities = resolveBtlActivityRoleCapabilities(role, row);
    return capabilities.canCancelEvent;
}

function patchBtlActivitiesInfiniteDataBtlStatus(
    data: InfiniteData<Array<BtlActivity>>,
    activityId: Uuid,
    btlStatus: BtlActivityStatus,
): InfiniteData<Array<BtlActivity>> {
    return {
        ...data,
        pages: data.pages.map((page) => page.map((r) => (r.id === activityId ? {...r, btlStatus} : r))),
    };
}

function patchBtlActivitiesRowsDataBtlStatus(
    oldData: unknown,
    activityId: Uuid,
    btlStatus: BtlActivityStatus,
): unknown {
    if (oldData === undefined || typeof oldData !== "object") {
        return oldData;
    }
    if (Array.isArray(oldData)) {
        return (oldData as Array<BtlActivity>).map((r) => (r.id === activityId ? {...r, btlStatus} : r));
    }
    const infinite = oldData as InfiniteData<Array<BtlActivity>>;
    if (!("pages" in infinite) || !Array.isArray(infinite.pages)) {
        return oldData;
    }
    return patchBtlActivitiesInfiniteDataBtlStatus(infinite, activityId, btlStatus);
}

function isBtlActivitiesDataViewerRowsQuery(queryKey: unknown): boolean {
    return (
        Array.isArray(queryKey) &&
        queryKey[0] === "ottopilotDataViewer" &&
        queryKey[1] === BTL_ACTIVITIES_OTTOPILOT_VIEWER_INSTANCE_ID &&
        queryKey.includes("rows") === true
    );
}

function RouteComponent() {
    const navigate = useNavigate();
    const search = useSearch({
        from: "/_withAuthenticationStatus/_authenticated/_withProfileStatus/_profileCompleted/w/$workspaceId/ottopilot/_ottopilotScaffold/btl-activities/",
    });
    const queryClient = useQueryClient();
    const {workspace} = useWorkspace();
    const workspaceId = workspace.id;
    const {intellsysBaseUrl} = useOttopilotConfiguration();
    const {heimdallUser} = useWorkspaceUser();
    const organizationalUnits = useOrganizationalUnits();
    const organizationalUnitTemplates = useOrganizationalUnitTemplates();
    const users = useUsers();
    const userTemplates = useUserTemplates();
    const btlActivityTemplates = useBtlActivityTemplates();
    const timezones = useTimezones();
    const btlModuleConfiguration = useOptionalWorkspaceCapabilityConfiguration(WorkspaceCapabilityType.ottopilotBtlActivities);
    const {showFlag} = useAnankeFlags();
    const showErrorFlag = useShowErrorFlag();
    const resolvedCalendarConfig = useMemo<SharedCalendarViewConfig>(() => {
        return btlModuleConfiguration?.calendarConfig ?? DEFAULT_BTL_CALENDAR_CONFIG;
    }, [btlModuleConfiguration?.calendarConfig]);
    const calendarLegendItems = useMemo(() => resolveCalendarLegendItems(resolvedCalendarConfig, DEFAULT_BTL_CALENDAR_CONFIG), [resolvedCalendarConfig]);
    const getCalendarTypeLabel = useCallback(
        (type: string): string => {
            return resolveCalendarEventTypeLabel(type, resolvedCalendarConfig, DEFAULT_BTL_CALENDAR_CONFIG, "Deleted");
        },
        [resolvedCalendarConfig],
    );
    const getCalendarTypeColor = useCallback(
        (type: string | undefined) => {
            return resolveCalendarEventTypeColor(type, resolvedCalendarConfig, DEFAULT_BTL_CALENDAR_CONFIG);
        },
        [resolvedCalendarConfig],
    );
    const getCalendarTemplateStrokeColor = useCallback(
        (templateId: Uuid) => {
            return resolveCalendarTemplateStrokeColor(templateId, resolvedCalendarConfig, DEFAULT_BTL_CALENDAR_CONFIG);
        },
        [resolvedCalendarConfig],
    );
    const btlActivitiesScopeFilterConfig = useMemo((): ScopeFilterConfig => {
        return {
            drawerTitle: "Type",
            selectionMode: "multi",
            options: [
                {
                    id: uniqueUuid`c2cbf106-0743-416a-9c76-5c92369a8328`,
                    type: "User",
                    label: "Assigned to me",
                    filterKey: "assignedusers",
                    filterType: "in",
                },
                {
                    id: uniqueUuid`e78e40a1-7203-45fe-b661-69dba389e88d`,
                    type: "User",
                    label: "Created by me",
                    filterKey: "createdBy",
                },
            ],
        };
    }, []);
    const lifecycleStageEventTemplateIds = btlModuleConfiguration?.lifecycleStageEventTemplateIds ?? [];
    const dhiraaguLifecycleTemplateIds: Array<Uuid> = [DhiraaguEventTypes.enquiry as Uuid, DhiraaguEventTypes.call as Uuid, DhiraaguEventTypes.sale as Uuid];
    const lifecycleConfigReferencesDhiraagu = dhiraaguLifecycleTemplateIds.some((id) => lifecycleStageEventTemplateIds.includes(id));
    const eventTemplates = useEventTemplates();
    const eventsModuleHasDhiraaguEnquiryTemplate = eventTemplates.some((et) => et.id === (DhiraaguEventTypes.enquiry as Uuid)) === true;
    /** Tiles can come from BTL `lifecycleStageEventTemplateIds` or from name-based fallback when that list is empty; enable forms if workspace is clearly Dhiraagu either way. */
    const isDhiraaguBtlLogActivityWorkspace = lifecycleConfigReferencesDhiraagu === true || eventsModuleHasDhiraaguEnquiryTemplate === true;

    const renderDhiraaguLogActivityLifecycleStageDetail = useCallback(
        ({
            eventTemplateId,
            onBack,
            productCatalogueId,
            associatedBeatId,
            initialIslandOuId,
        }: {
            eventTemplateId: string;
            stageLabel: string;
            productCatalogueId?: string | null;
            productName?: string | null;
            associatedBeatId?: string | null;
            initialIslandOuId?: string | null;
            onBack: () => void;
        }) => {
            const catalogueId: Uuid | null =
                productCatalogueId !== null && productCatalogueId !== undefined && productCatalogueId !== "" ? (productCatalogueId as Uuid) : null;
            if ((eventTemplateId as Uuid) === (DhiraaguEventTypes.enquiry as Uuid)) {
                return (
                    <DhiraaguEnquiryCreateFormEmbedded
                        initialProductCatalogueId={catalogueId}
                        initialAssociatedBeatId={associatedBeatId as Uuid | null | undefined}
                        initialIslandOuId={initialIslandOuId as Uuid | null | undefined}
                        onSuccess={onBack}
                        onCancel={onBack}
                    />
                );
            }
            if ((eventTemplateId as Uuid) === (DhiraaguEventTypes.call as Uuid)) {
                return (
                    <DhiraaguLogCallFormEmbedded
                        initialProductCatalogueId={catalogueId}
                        initialAssociatedBeatId={associatedBeatId as Uuid | null | undefined}
                        initialIslandOuId={initialIslandOuId as Uuid | null | undefined}
                        onSuccess={onBack}
                        onCancel={onBack}
                    />
                );
            }

            if ((eventTemplateId as Uuid) === (DhiraaguEventTypes.sale as Uuid)) {
                return (
                    <DhiraaguSaleCreateFormEmbedded
                        initialProductCatalogueId={catalogueId}
                        initialAssociatedBeatId={associatedBeatId as Uuid | null | undefined}
                        initialIslandOuId={initialIslandOuId as Uuid | null | undefined}
                        onSuccess={onBack}
                        onCancel={onBack}
                    />
                );
            }
            return null;
        },
        [],
    );

    // Necessary states
    const [openCreateModal, setOpenCreateModal] = useState<boolean>(false);

    useEffect(() => {
        if (search.view === "create") {
            setOpenCreateModal(true);
        }
    }, [search.view]);
    const [uploadModalOpen, setUploadModalOpen] = useState(false);
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [createPanelInitialTemplateId, setCreatePanelInitialTemplateId] = useState<Uuid | null>(null);

    const isMobile = useCheckMobile();
    const [optionsDrawerOpen, setOptionsDrawerOpen] = useState(false);
    const [exportDrawerOpen, setExportDrawerOpen] = useState(false);

    const handleUploadAndReview = async (file: File): Promise<Result<null>> => {
        try {
            // TODO: Replace with actual API call
            // Example API call:
            const formData = new FormData();
            formData.append("file", file);
            formData.append("workspaceId", workspace.id);
            const result = await robustJsonResultClientFetch(
                `${intellsysBaseUrl}/sub-system/pluto/api/csv/upload/btl-activities`,
                {
                    method: "POST",
                    body: formData,
                    headers: {Authorization: `Bearer ${heimdallUser.accessToken}`},
                },
                btlReviewItemApiResponseArraySchema,
            );
            if (result.success === false) {
                showErrorFlag("Upload failed", result.error.message);
                return errorResult(result.error);
            }
            // Cast validated data to the branded type - safe because zod validated the structure
            const apiResponse = result.data as Array<BtlReviewItemApiResponseItem>;
            console.log("data = ", result.data);

            // TODO: Replace with actual API response
            // const apiResponse: Array<BtlReviewItemApiType> = [];

            // Generate reviewId
            const reviewId = generateUuid();

            // Clear all existing items in the table before adding new data
            await clearAllBtlReviewItems();

            // Transform and store in IndexedDB
            const dbItems = transformApiResponseToDatabaseItems(apiResponse, reviewId);
            await addBtlReviewItems(dbItems);

            // Success - navigate to review page with reviewId
            await navigate({
                to: "/w/$workspaceId/ottopilot/btl-activities/review/$reviewId",
                params: {workspaceId: workspaceId, reviewId: reviewId},
            });
            return dataResult(null);
        } catch (err) {
            const message = err instanceof Error ? err.message : "An unexpected error occurred";
            showErrorFlag("Upload failed", message);
            return errorResult(err instanceof Error ? err : new Error(message));
        }
    };

    const [cancelEventDialogRow, setCancelEventDialogRow] = useState<BtlActivity | null>(null);
    const getEventTypeDisplayName = useCallback(
        (templateId: Uuid): string => {
            const configuredTemplate = btlActivityTemplates.find((template) => template.id === templateId);
            if (configuredTemplate !== undefined) {
                return configuredTemplate.displayName;
            }
            return btlActivityTemplateToDisplayName(templateId as never);
        },
        [btlActivityTemplates],
    );
    const btlActivityColumns: Array<OttopilotColumn<BtlActivity>> = [
        {
            id: "id",
            type: OttopilotColumnType.uuid,
            displayName: "ID",
            valueGetter: (activity) => activity.id,
        },
        {
            id: "title",
            type: OttopilotColumnType.string,
            displayName: "Title",
            valueGetter: (activity) => activity.title,
            sortingCapability: true,
            filteringCapability_contains: true,
        },
        {
            id: "eventName",
            type: OttopilotColumnType.string,
            displayName: "Event Name",
            valueGetter: (activity) =>
                `${getEventTypeDisplayName(activity.btlActivityTemplateId)}/${formatDateTime(activity.startDate as never)}/${getIslandNameFromGeographyNodes(activity.geographyNodes, organizationalUnits)}`,
            sortingCapability: false,
            filteringCapability_contains: false,
        },
        {
            id: "description",
            type: OttopilotColumnType.string,
            displayName: "Description",
            valueGetter: (activity) => activity.description,
            filteringCapability_contains: true,
        },
        {
            id: "templateId",
            type: OttopilotColumnType.string,
            displayName: "Event Type",
            valueGetter: (activity) => activity.btlActivityTemplateId,
            valueFormatter: (value) => {
                return getEventTypeDisplayName(value as Uuid);
            },
            sortingCapability: true,
            filteringCapability_contains: true,
        },
        {
            id: "btlStatus",
            type: OttopilotColumnType.string,
            displayName: "Status",
            valueGetter: (activity) => activity.btlStatus,
            valueFormatter: (value) => btlEventStatusToDisplayName(value as never),
            sortingCapability: true,
            filteringCapability_contains: true,
        },
        {
            id: "startDate",
            type: OttopilotColumnType.iso8601DateTime,
            displayName: "Start Date",
            valueGetter: (activity) => activity.startDate,
            valueFormatter: (value) => formatDateTime(value as never),
            sortingCapability: true,

            filteringCapability_contains: true,
        },
        {
            id: "endDate",
            type: OttopilotColumnType.iso8601DateTime,
            displayName: "End Date",
            valueGetter: (activity) => activity.endDate,
            valueFormatter: (value) => formatDateTime(value as never),
            sortingCapability: true,
            filteringCapability_contains: true,
        },
        {
            id: "isAlldayEvent",
            type: OttopilotColumnType.boolean,
            displayName: "All Day Event",
            valueGetter: (activity) => activity.isAlldayEvent,
            valueFormatter: (value) => (value ? "Yes" : "No"),
            sortingCapability: true,
            filteringCapability_contains: true,
        },
        {
            id: "timezone",
            type: OttopilotColumnType.string,
            displayName: "Timezone",
            valueGetter: (activity) => activity.timezone,
            valueFormatter: (value) => {
                const timezone = timezones[value as Uuid];
                return timezone !== undefined ? timezone.displayName : String(value);
            },
            sortingCapability: true,
            filteringCapability_contains: true,
        },
        {
            id: "assignmentMode",
            type: OttopilotColumnType.string,
            displayName: "Assignment Mode",
            valueGetter: (activity) => assignmentModeToDisplayName(activity.assignmentMode),
            sortingCapability: true,
            filteringCapability_contains: true,
        },
        {
            id: "createdBy",
            type: OttopilotColumnType.uuid,
            displayName: "Created By",
            valueGetter: (activity) => activity.createdBy,
            valueFormatter: (value) => getUserDisplayName(value as never, users, userTemplates),
            sortingCapability: true,
            filteringCapability_contains: true,
        },
        {
            id: "createdAt",
            type: OttopilotColumnType.iso8601DateTime,
            displayName: "Created At",
            valueGetter: (activity) => activity.createdAt,
            valueFormatter: (value) => formatDateTime(value as never),
            sortingCapability: true,
            filteringCapability_contains: true,
        },
        {
            id: "updatedBy",
            type: OttopilotColumnType.uuid,
            displayName: "Updated By",
            valueGetter: (activity) => activity.updatedBy,
            valueFormatter: (value) => getUserDisplayName(value as never, users, userTemplates),
            sortingCapability: true,
            filteringCapability_contains: true,
        },
        {
            id: "updatedAt",
            type: OttopilotColumnType.iso8601DateTime,
            displayName: "Updated At",
            valueGetter: (activity) => activity.updatedAt,
            valueFormatter: (value) => formatDateTime(value as never),
            sortingCapability: true,
            filteringCapability_contains: true,
        },
        {
            id: "geographyNode",
            displayName: "Island",
            type: OttopilotColumnType.array,
            valueGetter: (item) => getIslandOuIdsFromGeographyNodes(item.geographyNodes),
            valueFormatter: (ouIds: Array<string>) => getIslandNamesFromOuIds(ouIds, organizationalUnits),
        },
    ];

    const btlActivityDataViewController = useOttopilotDataViewerController<BtlActivity>({
        rowDisplayNameVariants: {
            singularSentenceCase: "Event",
            pluralSentenceCase: "Events",
            singularLowerCase: "event",
            pluralLowerCase: "events",
        },
        queryKeyPrefix: [BTL_ACTIVITIES_VIEWER_QUERY_KEY_PREFIX],
        calendarDataSource: "direct",
        scopeFilterConfig: btlActivitiesScopeFilterConfig,
        rowsQueryFn: async ({filterConfiguration, limit, offset, sortConfiguration, searchConfiguration}) => {
            const btlActivityResult = await getBtlActivities({
                workspaceId: workspace.id,
                offset: offset,
                limit: limit,
                filterConfiguration: filterConfiguration,
                sortConfiguration: sortConfiguration,
                searchConfiguration: searchConfiguration,
                intellsysBaseUrl: intellsysBaseUrl,
                accessToken: heimdallUser.accessToken,
            });
            if (btlActivityResult.success !== true) {
                throw btlActivityResult.error;
            }
            const btlApiActivities: Array<BtlActivityApiType> = btlActivityResult.data;
            const btlActivities = btlApiActivities.map((btlActivity) =>
                convertBtlActivityApiTypeToBtlActivity({
                    apiEvent: btlActivity,
                    users,
                    organizationalUnits,
                }),
            );

            return btlActivities;
        },
        rowCountQueryFn: async ({filterConfiguration, searchConfiguration}) => {
            const btlActivitiesRowCountResult = await getBtlActivities_Length({
                workspaceId: workspace.id,
                filterConfiguration: filterConfiguration,
                searchConfiguration: searchConfiguration,
                intellsysBaseUrl: intellsysBaseUrl,
                accessToken: heimdallUser.accessToken,
            });
            if (btlActivitiesRowCountResult.success !== true) {
                throw btlActivitiesRowCountResult.error;
            }
            const btlActivitiesLenght = btlActivitiesRowCountResult.data;

            return btlActivitiesLenght;
        },
        columnValuesQueryFn: async ({columnId}) => {
            if (columnId === "templateId") {
                return Array.from(new Set([...Object.values(BtlActivityTemplates), ...btlActivityTemplates.map((template) => template.id)]));
            }
            if (columnId === "btlStatus") {
                return Object.values(BtlActivityStatus).map((status) => status);
            }
            const columnValuesResult = await Promise.all(
                Object.values(BtlActivityTemplates).map((templateId) => {
                    return getBtlActivity_ColumnValues({
                        workspaceId: workspace.id,
                        columnId: columnId,
                        intellsysBaseUrl: intellsysBaseUrl,
                        accessToken: heimdallUser.accessToken,
                        templateId: templateId as Uuid,
                    });
                }),
            );
            const valuesSet = new Set<string>();
            for (const item of columnValuesResult) {
                if (item.success === false) {
                    throw item.error;
                }
                item.data.forEach((value) => valuesSet.add(value));
            }

            return [...valuesSet];
        },
        columns: {
            revisionId: uniqueUuid`0576005b-0c4e-4ebf-a1a3-2a92c88281e6`,
            lineItems: btlActivityColumns,
        },
        views: [
            {
                id: uniqueUuid`f74f6723-d5a2-4d84-9733-d1676d2408c1`,
                viewType: ViewType.table,
                visibleColumns: [
                    "title",
                    "eventName",
                    "templateId",
                    "btlStatus",
                    "startDate",
                    "endDate",
                    "assignmentMode",
                    "createdBy",
                    "createdAt",
                    "updatedAt",
                    // ...organizationalUnitTemplates.map(item => item.id),
                    "geographyNode",
                ],
            },
            {
                id: uniqueUuid`a1b2c3d4-e5f6-4a7b-8c9d-0e1f2a3b4c5d`,
                viewType: ViewType.kanbanBoard,
                filterFieldId: "btlStatus",
                columnConfig: [
                    {
                        id: uniqueUuid`37219b57-42f9-4678-81d2-2c24e5102c39`,
                        displayName: "Draft",
                        background: token("color.background.accent.gray.subtlest"),
                        textColor: token("color.text.accent.gray"),
                        possibleValues: [BtlActivityStatus.draft],
                    },
                    {
                        id: uniqueUuid`49268234-2c3b-4456-baee-e2d8fc2fa437`,
                        displayName: "Scheduled",
                        background: token("color.background.accent.yellow.subtlest"),
                        textColor: token("color.text.accent.yellow.bolder"),
                        possibleValues: [BtlActivityStatus.scheduled],
                    },
                    {
                        id: uniqueUuid`b138777b-5564-4046-9abe-c42bb6fc6730`,
                        displayName: "Live",
                        background: token("color.background.accent.blue.subtlest"),
                        textColor: token("color.text.accent.blue.bolder"),
                        possibleValues: [BtlActivityStatus.active],
                    },
                    {
                        id: uniqueUuid`b9f36da7-f8ae-4c6b-b84d-9f2b1448cdc2`,
                        displayName: "Completed",
                        background: token("color.background.accent.green.subtlest"),
                        textColor: token("color.text.accent.green.bolder"),
                        possibleValues: [BtlActivityStatus.completed],
                    },
                    {
                        id: uniqueUuid`c55c6e6d-7400-436f-914a-910f7caacd03`,
                        displayName: "Cancelled",
                        background: token("color.background.accent.red.subtlest"),
                        textColor: token("color.text.accent.red"),
                        possibleValues: [BtlActivityStatus.cancelled],
                    },
                ],
                cardRenderer: (activity: BtlActivity) => (
                    <BtlKanbanBoardCard
                        activity={activity}
                        users={users}
                        userTemplates={userTemplates}
                        organizationalUnits={organizationalUnits}
                        organizationalUnitTemplates={organizationalUnitTemplates}
                        onViewClick={() => {
                            btlActivityDataViewController.setCurrentlyOpenRowEngagementPanel({
                                rowEngagementType: RowEngagementType.read,
                                rowEngagementPanelType: RowEngagementPanelType.sidebar,
                                rowEngagementData: activity,
                            });
                        }}
                        onEditClick={() => {
                            btlActivityDataViewController.setCurrentlyOpenRowEngagementPanel({
                                rowEngagementType: RowEngagementType.update,
                                rowEngagementPanelType: RowEngagementPanelType.sidebar,
                                rowEngagementData: activity,
                            });
                        }}
                    />
                ),
            },
            {
                id: uniqueUuid`b2c3d4e5-f6a7-4b8c-9d0e-1f2a3b4c5d6e`,
                viewType: ViewType.calendar,
                getEventType: (activity) => activity.btlStatus,
                getEventTemplateLabel: (activity) => getEventTypeDisplayName(activity.btlActivityTemplateId),
                getEventTemplateStrokeColor: (activity) => getCalendarTemplateStrokeColor(activity.btlActivityTemplateId),
                onCreateEventClick: () => setOpenCreateModal(true),
                legendItems: calendarLegendItems,
                getEventTypeLabel: getCalendarTypeLabel,
                getEventColor: getCalendarTypeColor,
                allDayStyle: resolveCalendarAllDayStyle(resolvedCalendarConfig, DEFAULT_BTL_CALENDAR_CONFIG),
            },
        ],
        initialViewId: search.view === "kanban" ? BTL_KANBAN_VIEW_ID : search.view === "calendar" ? BTL_CALENDAR_VIEW_ID : BTL_TABLE_VIEW_ID,
        initialSortConfiguration: {
            revisionId: uniqueUuid`266fd377-6f59-4948-a984-6273c9d0cbb6`,
            lineItems: [
                {
                    columnId: "updatedAt",
                    direction: SortDirection.descending,
                },
            ],
        },
        initialVisibleFilters: {
            revisionId: uniqueUuid`db459f43-34c9-4d3d-9600-456c1f441ffb`,
            lineItems: ["title", "btlActivityTemplateId"],
        },
        searchColumns: ["title"],
        // pageSize: 10,
        createPanelComponent: () => (
            <CreateBtlActivityComponent
                initialTemplateId={createPanelInitialTemplateId}
                onSuccess={() => {
                    queryClient.invalidateQueries({
                        queryKey: ["ottopilotDataViewer", BTL_ACTIVITIES_VIEWER_QUERY_KEY_PREFIX, "rows"],
                    });
                    queryClient.invalidateQueries({
                        queryKey: ["ottopilotDataViewer", BTL_ACTIVITIES_VIEWER_QUERY_KEY_PREFIX, "rowCount"],
                    });
                    setCreatePanelInitialTemplateId(null);
                    btlActivityDataViewController.setCurrentlyOpenRowEngagementPanel({
                        rowEngagementType: RowEngagementType.none,
                        rowEngagementPanelType: RowEngagementPanelType.none,
                        rowEngagementData: null,
                    });
                }}
            />
        ),
        // deletePanelComponent: (row) => <div>TODO: Delete {row.title}</div>,
        readPanelComponent: (row, isExpanded) => (
            <BtlActivityViewPanelAlternative
                row={row}
                isExpanded={isExpanded}
                logActivityLifecycleStageDetail={isDhiraaguBtlLogActivityWorkspace === true ? renderDhiraaguLogActivityLifecycleStageDetail : undefined}
                onBtlActivityLocallyUpdated={({rowId, patch}) => {
                    btlActivityDataViewController.setCurrentlyOpenRowEngagementPanel((prev) => {
                        if (prev.rowEngagementData === null || prev.rowEngagementData.id !== rowId) {
                            return prev;
                        }
                        return {
                            ...prev,
                            rowEngagementData: {...prev.rowEngagementData, ...patch},
                        };
                    });
                }}
            />
        ),
        canEditRow: (_row: BtlActivity) => {
            return false;
        },
        // updatePanelComponent: (row, isExpanded) => (
        //     <BtlActivityEditRowEngagementPanel
        //         row={row}
        //         isExpanded={isExpanded}
        //         setCurrentlyOpenRowEngagementPanel={btlActivityDataViewController.setCurrentlyOpenRowEngagementPanel}
        //     />
        // ),
        rowEngagementDropdownOptions: (row: BtlActivity) => {
            const exportColumns = btlActivityColumns.filter((col) => col.id !== "id");
            const dataToExport = transformRowForExport(row, exportColumns);
            const currentUser = users.find((user) => user.userId === heimdallUser.id) ?? null;
            const canCurrentUserCancel = canCancelBtlActivity({
                row,
                currentUserId: heimdallUser.id as Uuid,
                currentUserTemplateId: currentUser?.templateId ?? null,
            });

            return [
                <ExportDataDropdownItem
                    key="export"


                    isVisible={false}
                />,
                <CopyDataDropdownItem
                    key="copy"
                    data={dataToExport}
                    showFlag={showFlag}
                />,
                ...(canCurrentUserCancel === true
                    ? [
                          <AnankeDropdownItem
                              key="cancel-event"
                              elemBefore={<CrossIcon label="" />}
                              onClick={() => {
                                  setCancelEventDialogRow(row);
                              }}
                          >
                              Cancel event
                          </AnankeDropdownItem>,
                      ]
                    : []),
            ];
        },
    });

    const {
        onExportPng: handleExportBtlActivitiesLikePng,
        onExportPdf: handleExportBtlActivitiesLikePdf,
    } = useOttopilotExportActions({
        controller: btlActivityDataViewController,

        pngFilename: "btl-activities-export.png",
        pdfFilename: "btl-activities-export.pdf",
        documentTitle: "Beat planning",
        csvExportMode: {kind: "disabled"},
        onError: (message) => showErrorFlag("Export failed", message),
    });

    const cancelEventMutation = useMutation({
        mutationFn: async (row: BtlActivity) => {
            const result = await cancelBtlActivity({
                workspaceId: workspace.id,
                id: row.id,
                templateId: row.btlActivityTemplateId,
                row,
                intellsysBaseUrl,
                accessToken: heimdallUser.accessToken,
            });
            if (result.success === false) {
                throw result.error;
            }
            return result.data;
        },
        onSuccess: (_data, row) => {
            showFlag({
                title: "Event cancelled",
                description: `"${row.title}" is now marked as cancelled.`,
                appearance: "success",
                isAutoDismiss: true,
                autoDismissSeconds: 5,
            });
            setCancelEventDialogRow(null);
            const patch: Partial<BtlActivity> = {
                btlStatus: BtlActivityStatus.cancelled,
                updatedAt: new Date().toISOString() as BtlActivity["updatedAt"],
            };
            btlActivityDataViewController.setCurrentlyOpenRowEngagementPanel((prev) => {
                if (prev.rowEngagementData === null || prev.rowEngagementData.id !== row.id) {
                    return prev;
                }
                return {
                    ...prev,
                    rowEngagementData: {...prev.rowEngagementData, ...patch},
                };
            });
            queryClient.setQueryData<Array<BtlActivity>>([WORKSPACE__BTL_ACTIVITIES__QUERY_KEY, workspace.id], (prev) => {
                if (prev === undefined) {
                    return prev;
                }
                return prev.map((r) => (r.id === row.id ? {...r, ...patch} : r));
            });
            queryClient.setQueriesData({predicate: (q) => isBtlActivitiesDataViewerRowsQuery(q.queryKey)}, (oldData) => {
                return patchBtlActivitiesRowsDataBtlStatus(oldData, row.id, BtlActivityStatus.cancelled);
            });
        },
        onError: (err) => {
            showFlag({
                title: "Could not cancel event",
                description: err instanceof Error ? err.message : "Something went wrong.",
                appearance: "error",
                isAutoDismiss: true,
                autoDismissSeconds: 8,
            });
        },
    });

    // Sync controller view when URL search.view changes (e.g. sidebar Table/Kanban/Calendar link)
    useEffect(() => {
        if (search.view === "create") {
            return;
        }
        const viewId = search.view === "kanban" ? BTL_KANBAN_VIEW_ID : search.view === "calendar" ? BTL_CALENDAR_VIEW_ID : BTL_TABLE_VIEW_ID;
        btlActivityDataViewController.setCurrentViewId(viewId);
    }, [search.view]);

    return (
        <>
            {/* <StackedUserAvatars userTemplates={userTemplates} users={users} /> */}
            <TableOnlyPage
                // breadcrumbs={["Journeys"]}
                title={"Beat planning"}
                hideTitleSection={
                    btlActivityDataViewController.currentlyOpenRowEngagementPanel.rowEngagementType === RowEngagementType.create &&
                    btlActivityDataViewController.currentlyOpenRowEngagementPanel.rowEngagementPanelType === RowEngagementPanelType.contentArea
                }
                titleRightSection={
                    <div
                        className={
                            isMobile
                                ? "grid grid-flow-col auto-cols-max gap-[8px] items-center"
                                : "grid grid-flow-col auto-cols-max gap-ds-space-100 items-center"
                        }
                    >
                        {isMobile ? (
                            <button
                                type="button"
                                onClick={() => setOpenCreateModal(true)}
                                className="w-[32px] h-[32px] flex items-center justify-center rounded-[3px]"
                                style={{
                                    backgroundColor: token("color.background.brand.bold", "#0052CC"),
                                    color: token("color.icon.inverse", "#FFFFFF"),
                                }}
                            >
                                <AddIcon label="Create event" />
                            </button>
                        ) : (
                            <AnankeButton
                                type="button"
                                appearance="primary"
                                iconBefore={(iconProps) => (
                                    <AddIcon
                                        {...iconProps}
                                        label=""
                                    />
                                )}
                                onClick={() => setOpenCreateModal(true)}
                            >
                                Create event
                            </AnankeButton>
                        )}
                        {isMobile ? (
                            <button
                                type="button"
                                onClick={() => setOptionsDrawerOpen(true)}
                                className="w-[32px] h-[32px] flex items-center justify-center rounded-[3px] border"
                                style={{
                                    borderColor: token("color.border", "#091E4224"),
                                    backgroundColor: token("elevation.surface", "#FFFFFF"),
                                    color: token("color.icon", "#44546F"),
                                }}
                            >
                                <ShowMoreVerticalIcon label="More actions" />
                            </button>
                        ) : (
                            <AnankeDropdownMenu<HTMLButtonElement>
                                placement="bottom-start"
                                trigger={({triggerRef, ...triggerProps}) => (
                                    <button
                                        {...triggerProps}
                                        ref={triggerRef}
                                        type="button"
                                        className="w-[32px] h-[32px] flex items-center justify-center rounded-[3px] border"
                                        style={{
                                            borderColor: token("color.border", "#091E4224"),
                                            backgroundColor: token("elevation.surface", "#FFFFFF"),
                                            color: token("color.icon", "#44546F"),
                                        }}
                                    >
                                        <ShowMoreVerticalIcon label="More actions" />
                                    </button>
                                )}
                            >
                                <AnankeDropdownItemGroup>
                                    <AnankeDropdownItem
                                        onClick={() => setUploadModalOpen(true)}
                                        elemBefore={
                                            <UploadIcon
                                                label=""
                                                color={token("color.icon")}
                                            />
                                        }
                                        elemAfter={
                                            <ChevronRightIcon
                                                label=""
                                                size="small"
                                                color={token("color.icon.subtle")}
                                            />
                                        }
                                    >
                                        Bulk upload
                                    </AnankeDropdownItem>
                                </AnankeDropdownItemGroup>
                                <AnankeDropdownItemGroup
                                    title="Export"
                                    hasSeparator={true}
                                >
                                    {/* <AnankeDropdownItem
                                        onClick={() => handleExportBtlActivitiesLikePng()}
                                        elemBefore={<DownloadIcon label="" color={token("color.icon")} />}
                                        elemAfter={
                                            <ChevronRightIcon label="" size="small" color={token("color.icon.subtle")} />
                                        }
                                    >
                                        Export as PNG
                                    </AnankeDropdownItem> */}
                                    <AnankeDropdownItem
                                        onClick={() => handleExportBtlActivitiesLikePdf()}
                                        elemBefore={
                                            <DownloadIcon
                                                label=""
                                                color={token("color.icon")}
                                            />
                                        }
                                        elemAfter={
                                            <ChevronRightIcon
                                                label=""
                                                size="small"
                                                color={token("color.icon.subtle")}
                                            />
                                        }
                                    >
                                        Export as PDF
                                    </AnankeDropdownItem>
                                </AnankeDropdownItemGroup>
                            </AnankeDropdownMenu>
                        )}
                    </div>
                }
                bottomSection={
                    <div className={`relative w-full h-full ${isMobile ? "[&_[data-view-picker]]:hidden" : ""}`}>
                        <OttopilotDataViewer controller={btlActivityDataViewController} />
                    </div>
                }
            />

            {/* Floating Action Button for Mobile */}
            {/* Removed as per UX updates */}

            {isMobile && (
                <>
                    <BottomDrawer
                        open={optionsDrawerOpen}
                        setOpen={setOptionsDrawerOpen}
                        title="Options"
                        fitContentOnOpen={true}
                    >
                        <InviteListOptionsDrawerContent
                            onBulkUpload={() => {
                                setOptionsDrawerOpen(false);
                                setUploadModalOpen(true);
                            }}
                            onExport={() => {
                                setOptionsDrawerOpen(false);
                                setExportDrawerOpen(true);
                            }}
                        />
                    </BottomDrawer>
                    <BottomDrawer
                        open={exportDrawerOpen}
                        setOpen={setExportDrawerOpen}
                        title="Export"
                        fitContentOnOpen={true}
                    >
                        <ExportOptionsDrawerContent
                            onExportAsPng={() => {
                                handleExportBtlActivitiesLikePng();
                                setExportDrawerOpen(false);
                            }}
                            onExportAsPdf={() => {
                                handleExportBtlActivitiesLikePdf();
                                setExportDrawerOpen(false);
                            }}
                        />
                    </BottomDrawer>
                </>
            )}

            {/* Adaptive CSV Upload (works on both mobile and desktop) */}
            <CsvUploadComponent
                opened={uploadModalOpen}
                onClose={() => setUploadModalOpen(false)}
                selectedFile={selectedFile}
                onFileChange={setSelectedFile}
                title="Bulk upload beat planning"
                subtitle="Upload a .csv file to review and create multiple events at once"
                templateConfig={BTL_CSV_TEMPLATE_CONFIG}
                onUploadAndReview={handleUploadAndReview}
            />

            {/* Adaptive Create Event Modal (works on both mobile and desktop) */}
            <CreateBtlEventAdaptiveDialog
                isOpen={openCreateModal}
                onClose={() => setOpenCreateModal(false)}
                onContinue={(templateId) => {
                    setCreatePanelInitialTemplateId(templateId);
                    btlActivityDataViewController.setCurrentlyOpenRowEngagementPanel({
                        rowEngagementType: RowEngagementType.create,
                        rowEngagementPanelType: RowEngagementPanelType.fullscreen,
                        rowEngagementData: null,
                    });
                    setOpenCreateModal(false);
                }}
            />

            <CancelEventConfirmationModal
                opened={cancelEventDialogRow !== null}
                onClose={() => setCancelEventDialogRow(null)}
                onConfirmCancel={() => {
                    if (cancelEventDialogRow === null) {
                        return;
                    }
                    cancelEventMutation.mutate(cancelEventDialogRow);
                }}
                isConfirmPending={cancelEventMutation.isPending === true}
            />
        </>
    );
}
